import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { api } from "@/lib/api";
import PublicLayout, { PrimaryBtn, GhostBtn, H2 } from "@/components/public/PublicLayout";
import { ROUTES, C, SERIF } from "@/lib/publicLinks";
import { useCart, imgUrl } from "@/context/CartContext";

export default function Cart() {
  const { items, setQty, remove, subtotal } = useCart();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const checkout = async () => {
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/shop/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        email, name, return_base: window.location.origin,
      });
      if (data.checkout_url) window.location.href = data.checkout_url;
      else setErr("Could not start checkout. Please try again.");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Checkout failed. Please try again.");
    } finally { setBusy(false); }
  };

  if (items.length === 0) return (
    <PublicLayout>
      <div className="max-w-3xl mx-auto px-6 py-20 text-center" data-testid="cart-empty">
        <ShoppingBag className="h-12 w-12 mx-auto text-slate-300" />
        <h1 className="mt-5 text-3xl text-slate-900" style={SERIF}>Your cart is empty</h1>
        <p className="mt-2 text-slate-600">Explore practitioner-selected KISSED wellness products.</p>
        <div className="mt-7 flex justify-center"><PrimaryBtn to={ROUTES.shop}>Continue Shopping</PrimaryBtn></div>
      </div>
    </PublicLayout>
  );

  return (
    <PublicLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <H2>Your Cart</H2>
        <div className="mt-8 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-3" data-testid="cart-items">
            {items.map((i) => (
              <div key={i.product_id} className="flex items-center gap-4 rounded-2xl bg-white p-4" style={{ border: `1px solid ${C.line}` }} data-testid={`cart-item-${i.product_id}`}>
                <div className="h-20 w-20 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                  {i.image ? <img src={imgUrl(i.image)} alt={i.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-300"><ShoppingBag className="h-6 w-6" /></div>}
                </div>
                <div className="flex-1 min-w-0">
                  <Link to={`/shop/product/${i.slug}`} className="font-semibold text-slate-900 hover:underline" style={SERIF}>{i.name}</Link>
                  <div className="text-sm text-slate-500 mt-0.5">${Number(i.price).toFixed(2)} each</div>
                </div>
                <div className="inline-flex items-center rounded-full border" style={{ borderColor: C.line }}>
                  <button onClick={() => setQty(i.product_id, i.quantity - 1)} className="px-2.5 py-2" data-testid={`cart-dec-${i.product_id}`}><Minus className="h-4 w-4" /></button>
                  <span className="px-3 text-sm font-semibold" data-testid={`cart-qty-${i.product_id}`}>{i.quantity}</span>
                  <button onClick={() => setQty(i.product_id, i.quantity + 1)} className="px-2.5 py-2" data-testid={`cart-inc-${i.product_id}`}><Plus className="h-4 w-4" /></button>
                </div>
                <div className="w-20 text-right font-semibold text-slate-900">${(i.price * i.quantity).toFixed(2)}</div>
                <button onClick={() => remove(i.product_id)} className="text-slate-400 hover:text-red-500" data-testid={`cart-remove-${i.product_id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            <Link to={ROUTES.shop} className="inline-block mt-2 text-sm font-medium" style={{ color: C.primary }}>← Continue shopping</Link>
          </div>

          <div className="rounded-2xl bg-white p-6 h-fit" style={{ border: `1px solid ${C.line}` }} data-testid="cart-summary">
            <div className="text-lg text-slate-900" style={SERIF}>Order Summary</div>
            <div className="mt-4 flex justify-between text-sm text-slate-600"><span>Subtotal</span><span data-testid="cart-subtotal">${subtotal.toFixed(2)}</span></div>
            <div className="mt-1 text-xs text-slate-400">Taxes calculated at Square checkout.</div>
            <div className="mt-5 space-y-3">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name (optional)" className="w-full rounded-lg border px-3 py-2.5 text-sm" style={{ borderColor: C.line }} data-testid="checkout-name" />
              <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email for your receipt" className="w-full rounded-lg border px-3 py-2.5 text-sm" style={{ borderColor: C.line }} data-testid="checkout-email" />
            </div>
            {err && <div className="mt-3 text-sm text-red-600" data-testid="checkout-error">{err}</div>}
            <button onClick={checkout} disabled={busy} className="mt-5 w-full rounded-full px-6 py-3 text-sm font-semibold text-white disabled:opacity-60" style={{ background: C.primary }} data-testid="checkout-button">
              {busy ? "Starting secure checkout…" : "Checkout with Square"}
            </button>
            <p className="mt-3 text-[11px] text-center text-slate-400">Secure payment powered by Square.</p>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
